import { useEffect, useState } from "react";
import { KeyRound, Pencil, Plus } from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import usePoll from "@/hooks/usePoll";
import { useAuth } from "@/context/AuthContext";
import { api, dt, errMsg, ROLE_LABEL } from "@/lib/api";
import { ErrorBox, Field, Loading, Modal, PageHeader, Sel } from "@/components/common";

function UserForm({ open, onOpenChange, initial, meta, onDone }) {
  const isNew = !initial?.id;
  const { settings } = useAuth();
  const [f, setF] = useState(() => initial?.id ? { ...initial, email: initial.email || "", phone: initial.phone || "", category_scope: initial.category_scope || [], section_ids: initial.section_ids || [] }
    : { username: "", full_name: "", email: "", phone: "", role: "clerk", password: "", permissions: meta.role_defaults.clerk, category_scope: [], section_ids: [], active: true });
  const [sections, setSections] = useState([]);
  useEffect(() => { api.get("/sections").then((r) => setSections(r.data.filter((s) => s.active !== false))).catch(() => {}); }, []);
  const toggleSec = (id) => setF({ ...f, section_ids: f.section_ids.includes(id) ? f.section_ids.filter((x) => x !== id) : [...f.section_ids, id] });
  const [err, setErr] = useState("");
  const setRole = (r) => setF({ ...f, role: r, permissions: meta.role_defaults[r] });
  const toggle = (p) => setF({ ...f, permissions: f.permissions.includes(p) ? f.permissions.filter((x) => x !== p) : [...f.permissions, p] });
  const toggleCat = (c) => setF({ ...f, category_scope: f.category_scope.includes(c) ? f.category_scope.filter((x) => x !== c) : [...f.category_scope, c] });
  const save = async () => {
    setErr("");
    try {
      if (isNew) await api.post("/users", { ...f, email: f.email || null, phone: f.phone || null });
      else await api.put(`/users/${f.id}`, { full_name: f.full_name, email: f.email || "", phone: f.phone || null, role: f.role, permissions: f.permissions, category_scope: f.category_scope, section_ids: f.section_ids, active: f.active });
      toast.success("تم حفظ المستخدم"); onOpenChange(false); onDone();
    } catch (e) { setErr(errMsg(e)); }
  };
  const inp = (k, p = {}) => <input className="k-input" value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`user-form-${k}`} {...p} />;
  return (
    <Modal open={open} onOpenChange={onOpenChange} wide title={isNew ? "مستخدم جديد" : `تعديل ${initial.full_name}`}
      footer={<button className="k-btn-primary" onClick={save} data-testid="user-form-save">حفظ</button>}>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="الاسم الكامل *">{inp("full_name")}</Field>
        <Field label="اسم المستخدم *" hint="أحرف لاتينية وأرقام">{inp("username", { disabled: !isNew, dir: "ltr" })}</Field>
        <Field label="البريد الإلكتروني">{inp("email", { dir: "ltr" })}</Field>
        <Field label="الهاتف">{inp("phone")}</Field>
        <Field label="نوع الحساب"><Sel value={f.role} onChange={setRole} testid="user-form-role">{Object.entries(meta.roles).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</Sel></Field>
        {isNew ? <Field label="كلمة مرور مؤقتة *" hint="سيُطلب تغييرها عند أول دخول">{inp("password", { type: "text", dir: "ltr" })}</Field>
          : <Field label="الحالة"><Sel value={f.active ? "1" : "0"} onChange={(v) => setF({ ...f, active: v === "1" })} testid="user-form-active"><option value="1">نشط</option><option value="0">معطل</option></Sel></Field>}
      </div>
      {f.role !== "admin" ? (
        <div className="mt-2">
          <div className="k-label">الصلاحيات</div>
          <div className="grid sm:grid-cols-2 gap-2 max-h-64 overflow-y-auto border rounded-xl p-3">
            {Object.entries(meta.permissions).map(([k, v]) => (
              <label key={k} className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={f.permissions.includes(k)} onCheckedChange={() => toggle(k)} data-testid={`perm-${k}`} /> {v}
              </label>
            ))}
          </div>
        </div>
      ) : <div className="text-sm text-slate-500 mt-2">المدير يملك جميع الصلاحيات.</div>}
      {f.role !== "admin" && (settings?.sale_categories || []).length > 0 && (
        <div className="mt-2" data-testid="user-category-scope">
          <div className="k-label">حصر الاطلاع على مبيعات فئات معينة (فارغ = كل المبيعات)</div>
          <div className="flex flex-wrap gap-4 border rounded-xl p-3">
            {settings.sale_categories.map((c) => (
              <label key={c} className="flex items-center gap-2 text-sm cursor-pointer"><Checkbox checked={f.category_scope.includes(c)} onCheckedChange={() => toggleCat(c)} data-testid={`scope-${c}`} /> {c}</label>
            ))}
          </div>
        </div>
      )}
      {["pen_worker", "barn_worker"].includes(f.role) && (
        <div className="mt-2" data-testid="user-sections">
          <div className="k-label">الأقسام التي يعمل بها (يسجل الاختيار منها فقط)</div>
          <div className="flex flex-wrap gap-4 border rounded-xl p-3">
            {sections.length ? sections.map((s) => (
              <label key={s.id} className="flex items-center gap-2 text-sm cursor-pointer"><Checkbox checked={f.section_ids.includes(s.id)} onCheckedChange={() => toggleSec(s.id)} data-testid={`user-section-${s.code}`} /> {s.code} – {s.name}</label>
            )) : <span className="text-sm text-slate-500">لا توجد أقسام</span>}
          </div>
          <div className="text-xs text-slate-500 mt-1">يُضاف تلقائيًا كل قسم يكون هذا العامل مسؤوله في «أقسام الحظيرة».</div>
        </div>
      )}
      <ErrorBox text={err} />
    </Modal>
  );
}

export default function UsersPage() {
  const { user: me } = useAuth();
  const { data, error, reload } = usePoll("/users", null, 0);
  const [meta, setMeta] = useState(null);
  const [form, setForm] = useState(null);
  const [reset, setReset] = useState(null);
  const [pw, setPw] = useState("");
  useEffect(() => { api.get("/meta/permissions").then((r) => setMeta(r.data)); }, []);
  if (error) return <ErrorBox text={error} />;
  if (!data || !meta) return <Loading />;
  const doReset = async () => {
    try { await api.post(`/users/${reset.id}/reset-password`, { new_password: pw }); toast.success("تم تعيين كلمة مرور مؤقتة"); setReset(null); setPw(""); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <PageHeader title="المستخدمون والصلاحيات" subtitle="الحسابات تُنشأ من طرف المدير فقط، ويُتحقق من الصلاحيات على الخادم">
        <button className="k-btn-primary" onClick={() => setForm({})} data-testid="add-user-button"><Plus className="w-5 h-5" /> مستخدم جديد</button>
      </PageHeader>
      <div className="k-card overflow-x-auto"><table className="k-table" data-testid="users-table">
        <thead><tr><th>الاسم</th><th>اسم المستخدم</th><th>الدور</th><th>الصلاحيات</th><th>الحالة</th><th>آخر دخول</th><th></th></tr></thead>
        <tbody>{data.map((u) => (
          <tr key={u.id} className={u.active ? "" : "opacity-50"} data-testid={`user-row-${u.username}`}>
            <td className="font-bold">{u.full_name}</td><td dir="ltr" className="text-right">{u.username}</td><td>{ROLE_LABEL[u.role]}</td>
            <td className="text-xs max-w-xs">{u.role === "admin" ? "جميع الصلاحيات" : u.effective_permissions.map((p) => meta.permissions[p]).join("، ")}{u.category_scope?.length > 0 && <div className="font-bold text-[#B45309] mt-1">الفئات: {u.category_scope.join("، ")}</div>}</td>
            <td>{u.active ? "نشط" : "معطل"}{u.must_change_password && <span className="block text-xs text-amber-700">بانتظار تغيير كلمة المرور</span>}</td>
            <td className="text-xs">{dt(u.last_login)}</td>
            <td className="whitespace-nowrap">
              <button className="k-btn-outline k-btn-sm" onClick={() => setForm(u)} data-testid={`edit-user-${u.username}`}><Pencil className="w-4 h-4" /></button>
              {u.id !== me.id && <button className="k-btn-outline k-btn-sm mr-1" onClick={() => setReset(u)} data-testid={`reset-user-${u.username}`}><KeyRound className="w-4 h-4" /></button>}
            </td>
          </tr>))}</tbody>
      </table></div>
      {form && <UserForm open onOpenChange={(o) => !o && setForm(null)} initial={form.id ? form : null} meta={meta} onDone={reload} />}
      <Modal open={!!reset} onOpenChange={(o) => !o && setReset(null)} title={`كلمة مرور مؤقتة لـ ${reset?.full_name}`} description="سيُطلب من المستخدم تغييرها عند الدخول، وتُنهى جلساته الحالية"
        footer={<button className="k-btn-primary" disabled={pw.length < 8} onClick={doReset} data-testid="reset-password-save">تعيين</button>}>
        <input className="k-input" dir="ltr" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="8 أحرف على الأقل" data-testid="reset-password-input" />
      </Modal>
    </div>
  );
}
